import { useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { errorMessage } from '../members/hooks';
import type { SecuritySlot } from './types';

const MIN_REASON = 10;

/** Bloqueo por seguridad de un QR (R1.8): advertencia neutra al escanear; solo el operador lo levanta. */
export function SecurityBlockPanel() {
  const [code, setCode] = useState('');
  const [domain, setDomain] = useState('q-r.cl');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'block' | 'unblock' | null>(null);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [touched, setTouched] = useState(false);

  const reasonError = reason.trim().length < MIN_REASON ? `El motivo es obligatorio (al menos ${MIN_REASON} caracteres).` : undefined;
  const codeError = !/^[A-Za-z0-9]{1,12}$/.test(code.trim()) ? 'Escribe el código del QR.' : undefined;

  async function run(action: 'block' | 'unblock', e?: FormEvent) {
    e?.preventDefault();
    setTouched(true);
    setResult(null);
    if (reasonError || codeError) return;
    setBusy(action);
    try {
      const slot = await api.post<SecuritySlot>(`/v1/operator/qrs/${encodeURIComponent(code.trim().toUpperCase())}/security-${action}`, {
        domain: domain.trim() || null,
        reason: reason.trim(),
      });
      setResult({
        tone: 'success',
        text:
          action === 'block'
            ? slot.changed === false
              ? `${slot.code} ya estaba bloqueado por seguridad.`
              : `Bloqueamos ${slot.code}. Quien lo escanee verá una advertencia y no será redirigido.`
            : `Levantamos el bloqueo de ${slot.code}. Quedó en estado ${slot.state}.`,
      });
      setReason('');
      setTouched(false);
    } catch (err) {
      setResult({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <form onSubmit={(e) => void run('block', e)} className="space-y-3 rounded-lg border border-line bg-white p-4" noValidate aria-labelledby="bloqueo-titulo">
      <h2 id="bloqueo-titulo" className="font-display text-base font-bold text-ink">Bloqueo por seguridad de un QR</h2>
      <p className="text-xs text-ink-muted">Muestra una advertencia neutra al escanear y no redirige. Los QR de la organización siguen funcionando.</p>
      <div className="grid grid-cols-[1fr_8rem] gap-3">
        <Input label="Código" mono value={code} onChange={(e) => setCode(e.target.value)} error={touched ? codeError : undefined} />
        <Input label="Dominio" value={domain} onChange={(e) => setDomain(e.target.value)} />
      </div>
      <Input label="Motivo" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} error={touched ? reasonError : undefined} hint="Queda en la auditoría." />
      <div aria-live="polite">{result && <Banner tone={result.tone}>{result.text}</Banner>}</div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" loading={busy === 'unblock'} disabled={busy !== null} onClick={() => void run('unblock')}>Levantar bloqueo</Button>
        <Button type="submit" variant="danger" loading={busy === 'block'} disabled={busy !== null}>Bloquear</Button>
      </div>
    </form>
  );
}
